import { createStore } from "../../../util/zustand";

export enum EditorSaveStatus {
    SAVED = "SAVED",
    SAVING = "SAVING",
    ERROR = "ERROR",
}

type EditorSaveState = {
    // The current save status of the editor
    saveStatus: EditorSaveStatus | undefined;

    // Timestamp of the last successful save in milliseconds since epoch
    lastSavedAt: number | null;

    // Whether the live editor doc is empty. Guards (e.g. the "Next Step" button) read this
    // instead of the saved prop, which lags behind the debounced autosave.
    isEmpty: boolean;

    // Saves the editor's debounced edits now and resolves once persisted
    flushPendingSave: (() => Promise<void>) | null;
};

const UNTRACKED_EDITOR_STATE: EditorSaveState = {
    saveStatus: undefined,
    lastSavedAt: null,
    isEmpty: true,
    flushPendingSave: null,
};

// Save state for editors that need to be read outside the component, keyed by editor id.
const EditorStore = createStore<{ editors: Record<string, EditorSaveState> }>({ editors: {} });

export const setEditorSaveState = (editorId: string, patch: Partial<EditorSaveState>) => {
    EditorStore.setState(state => ({
        editors: { ...state.editors, [editorId]: { ...(state.editors[editorId] ?? UNTRACKED_EDITOR_STATE), ...patch } },
    }));
};

export const clearEditorSaveState = (editorId: string) => {
    EditorStore.setState(state => {
        const { [editorId]: _cleared, ...editors } = state.editors;
        return { editors };
    });
};

export const getEditorSaveState = (editorId: string) => EditorStore.getState().editors[editorId] ?? UNTRACKED_EDITOR_STATE;

// Hook to read a specific piece of editor save state for a given editor id.
export const useEditorSaveState = <Key extends keyof EditorSaveState>(editorId: string, key: Key) =>
    EditorStore.use(state => (state.editors[editorId] ?? UNTRACKED_EDITOR_STATE)[key]);

// Saves every mounted editor's debounced edits now and resolves once persisted. Call it before
// anything that reads the saved content (sending a draft, applying a template, leaving the editor).
export const flushPendingEditorSaves = async () => {
    const { editors } = EditorStore.getState();
    await Promise.all(Object.values(editors).map(editor => editor.flushPendingSave?.()));
};

export const focusEditor = (editorId?: string, position: "start" | "end" | boolean = "end") => {
    window.dispatchEvent(new CustomEvent("tiptap:focus", { detail: { editorId, position } }));
};
